import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Bell, BellOff, Check, AlertTriangle, ArrowLeftRight, RefreshCw } from 'lucide-react';
import logger from '../utils/logger';

const ICON_MAP = {
  conflict: AlertTriangle,
  shift_request: ArrowLeftRight,
  request_update: RefreshCw,
  mandatory_alert: AlertTriangle,
  call_out: AlertTriangle,
};

export default function NotificationsPage() {
  const { api } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchNotifications(); }, [fetchNotifications]);

  const markRead = async (id) => {
    await api.put(`/notifications/${id}/read`);
    fetchNotifications();
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    fetchNotifications();
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="p-4 sm:p-8 space-y-6" data-testid="notifications-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl tracking-tight font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Notifications
          </h1>
          <p className="text-base text-[#5A4D6E] mt-1">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </p>
        </div>
        {unreadCount > 0 && (
          <Button variant="outline" onClick={markAllRead} className="border-[#DED8EB] text-[#5A4D6E] gap-2" data-testid="mark-all-read-btn">
            <Check className="h-4 w-4" /> Mark all read
          </Button>
        )}
      </div>

      {(() => {
        if (loading) return <div className="text-center py-12 text-[#8E85A0]">Loading...</div>;
        if (notifications.length === 0) return (
          <Card className="border-[#DED8EB] shadow-sm bg-white">
            <CardContent className="py-12 text-center">
              <BellOff className="h-10 w-10 mx-auto mb-3 text-[#8E85A0] opacity-50" />
              <p className="text-[#8E85A0]">No notifications yet</p>
            </CardContent>
          </Card>
        );
        return (
        <div className="space-y-2">
          {notifications.map((notif) => {
            const Icon = ICON_MAP[notif.type] || Bell;
            return (
              <Card
                key={notif.id}
                className={`border shadow-sm transition-all duration-200 hover:-translate-y-px cursor-pointer ${
                  notif.read ? 'border-[#DED8EB] bg-white opacity-70' : 'border-[#9B7ED8] bg-[#EDE7F6]/30'
                }`}
                onClick={() => !notif.read && markRead(notif.id)}
                data-testid={`notification-${notif.id}`}
              >
                <CardContent className="p-4 flex items-start gap-4">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                    (() => {
                      if (notif.type === 'conflict') return 'bg-orange-50 text-[#F4A261]';
                      if (notif.type === 'request_update') return 'bg-green-50 text-[#2E7D32]';
                      if (notif.type === 'mandatory_alert' || notif.type === 'call_out') return 'bg-red-50 text-red-600';
                      return 'bg-[#F0EDF6] text-[#5A4D6E]';
                    })()
                  }`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${notif.read ? 'text-[#5A4D6E]' : 'text-[#2D1B4E] font-medium'}`}>
                      {notif.message}
                    </p>
                    <p className="text-xs text-[#8E85A0] mt-1" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      {new Date(notif.created_at).toLocaleString()}
                    </p>
                  </div>
                  {!notif.read && (
                    <div className="w-2 h-2 rounded-full bg-[#E07A5F] shrink-0 mt-2" />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )})()}
    </div>
  );
}
